// ProductsMenuItemInline.tsx
import { memo, useCallback, useEffect, useState } from 'react'
import { SidebarMenuSubButton, SidebarMenuSubItem } from '@/components/ui/sidebar'
import { Product, selectedProductAtom } from '../state/selectProduct'
import { useAtom } from 'jotai'
import { cn } from '@/lib/utils'
import { getStatusColor, produtosLabels } from '../utils'
import { friendlyNameMaps } from '@/components/form/input/StatusMapper'
import { TypeContrato } from '@/features/agreement/utils/enum'
import { formatRegimeTitle } from '@/utils/format'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ChevronRight } from 'lucide-react'
type ProductsMenuItemProps = {
  subItem: {
    title: string
    url?: string
    icon?: React.ReactNode
    products?: Array<Product>
  }
  keepOpen?: boolean
  handleClickItem: () => void
}

export const ProductsMenuItem = memo(({ subItem, keepOpen = false, handleClickItem }: ProductsMenuItemProps) => {
  const [, setSelectedProduct] = useAtom(selectedProductAtom)

  if (!subItem.products || subItem.products.length === 0) return null

  const handleProductClick = useCallback(
    (product: Product) => {

      setSelectedProduct(product)
      handleClickItem()
    },
    [setSelectedProduct, handleClickItem]
  )
  const [open, setOpen] = useState(true)

  useEffect(() => {
    setOpen(keepOpen)
  }, [keepOpen, subItem.url])

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="w-full">
      {/* Cabeçalho do grupo vira TRIGGER */}
      <CollapsibleTrigger asChild>
        <SidebarMenuSubItem asChild className=" pb-1 cursor-pointer select-none">
          <button
            type="button"
            className="
              group flex w-full items-center gap-2 rounded-md
              text-[10px] font-semibold uppercase tracking-wide
              text-muted-foreground hover:text-accent-foreground
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background
            "
          >
            <span className="truncate ml-2">{formatRegimeTitle(subItem.title as TypeContrato)}</span>
            <ChevronRight
              className="
                size-3 transition-transform
                data-[state=open]:rotate-90
              "
              // pega o data-state do Collapsible automaticamente pelo Trigger
              data-state={open ? 'open' : 'closed'}
            />
          </button>
        </SidebarMenuSubItem>
      </CollapsibleTrigger>

      {/* Conteúdo recolhível: lista de produtos */}
      <CollapsibleContent
        className="
          overflow-hidden
          data-[state=closed]:animate-collapsible-up
          data-[state=open]:animate-collapsible-down
        "
      >
        {subItem.products?.length ? (
          subItem.products.map(product => (
            <SidebarMenuSubButton
              key={product.id}
              className="
                group/product flex w-full items-center gap-2 cursor-pointer py-2
                hover:bg-accent hover:text-primary-foreground
              "
              onClick={() => handleProductClick(product)}
            >
              <span className="text-sm font-medium truncate ">{produtosLabels[product.typeServico]}</span>
              <div className="ml-auto flex items-center">
                {/* bolinha de status */}
                <span
                  className={cn(
                    'h-2.5 w-2.5 rounded-full border transition-opacity duration-200',
                    'group-hover/product:opacity-0',
                    getStatusColor(product.status)?.replace('text-', 'border-').replace('bg-', 'bg-')
                  )}
                />
                {/* label de status expandindo no hover */}
                <span
                  className={cn(
                    'max-w-0 overflow-hidden rounded-full border text-[10px] leading-none opacity-0 text-black',
                    'px-0 py-[2px] transition-all duration-200 ease-out',
                    'group-hover/product:max-w-[140px] group-hover/product:px-2 group-hover/product:opacity-80',
                    getStatusColor(product.status)
                  )}
                >
                  {friendlyNameMaps['produtosStatus'][product.status] ?? product.status}
                </span>
              </div>
            </SidebarMenuSubButton>
          ))
        ) : (
          <div className="px-2.5 py-2 text-xs text-muted-foreground">Nenhum produto.</div>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
})

ProductsMenuItem.displayName = 'ProductsMenuItem'
